"use client";

import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  Mail,
  QrCode,
  Sparkles,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth-client";

interface LoginCardProps {
  redirectUrl?: string;
}

export function LoginCard({ redirectUrl = "/me" }: LoginCardProps) {
  const [email, setEmail] = useState("");
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingMagicLink, setIsLoadingMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [lastUsedMethod, setLastUsedMethod] = useState<string | null>(null);

  const oneTapTriggeredRef = useRef(false);

  // Detecta o último método de login utilizado
  useEffect(() => {
    try {
      const method = authClient.getLastUsedLoginMethod();
      if (method) {
        setLastUsedMethod(method);
      }
    } catch (e) {
      console.debug("Não foi possível carregar último método de login:", e);
    }
  }, []);

  // Contador regressivo para reenvio de e-mail (cooldown)
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  // Aciona o Google One Tap apenas uma vez e captura o clique fora silenciosamente
  useEffect(() => {
    if (oneTapTriggeredRef.current) return;

    if (process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID) {
      oneTapTriggeredRef.current = true;
      authClient
        .oneTap({
          callbackURL: redirectUrl,
          cancelOnTapOutside: true,
        })
        .catch((err) => {
          // O usuário fechou ou clicou fora do prompt — comportamento normal
          console.debug("Google One Tap dispensado:", err);
        });
    }
  }, [redirectUrl]);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoadingGoogle(true);
      setErrorMessage(null);
      const res = await authClient.signIn.social({
        provider: "google",
        callbackURL: redirectUrl,
      });
      if (res?.error) {
        setErrorMessage(
          res.error.message ||
            "Não foi possível conectar com o Google. Verifique se GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET estão definidos no .env.local.",
        );
        setIsLoadingGoogle(false);
      }
    } catch (err) {
      console.error("Erro no login com Google:", err);
      setErrorMessage(
        "Erro ao conectar com o Google. Verifique as credenciais no .env.local.",
      );
      setIsLoadingGoogle(false);
    }
  };

  const handleMagicLinkSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setErrorMessage("Por favor, informe um endereço de e-mail válido.");
      return;
    }

    try {
      setIsLoadingMagicLink(true);
      setErrorMessage(null);

      const result = await authClient.signIn.magicLink({
        email,
        callbackURL: redirectUrl,
      });

      if (result.error) {
        setErrorMessage(
          result.error.message || "Erro ao enviar o link de acesso.",
        );
      } else {
        setMagicLinkSent(true);
        setCooldown(60);
      }
    } catch (err) {
      console.error("Erro no magic link:", err);
      setErrorMessage("Ocorreu uma falha ao enviar o link de acesso.");
    } finally {
      setIsLoadingMagicLink(false);
    }
  };

  return (
    <Card className="w-full max-w-md border-border/60 bg-card/90 shadow-2xl backdrop-blur-md">
      <CardHeader className="text-center pb-4">
        <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/25">
          <QrCode className="size-6" />
        </div>
        <CardTitle className="text-2xl font-bold tracking-tight">
          Entrar na TagFull
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground">
          Acesse suas Smart Tags, medalhas de identificação e painel
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {errorMessage && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-xs text-destructive">
            {errorMessage}
          </div>
        )}

        {magicLinkSent ? (
          <div className="space-y-4 text-center py-4">
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500 ring-1 ring-emerald-500/20">
              <CheckCircle2 className="size-8" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-foreground">
                Link de acesso enviado!
              </h3>
              <p className="text-xs text-muted-foreground px-4">
                Enviamos um link mágico para{" "}
                <span className="font-medium text-foreground">{email}</span>.
                Clique no link recebido para entrar instantaneamente.
              </p>
            </div>
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={cooldown > 0}
                onClick={() => setMagicLinkSent(false)}
                className="text-xs"
              >
                {cooldown > 0
                  ? `Aguarde ${cooldown}s para reenviar`
                  : "Reenviar ou usar outro e-mail"}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="relative">
              {lastUsedMethod === "google" && (
                <span className="absolute -top-2.5 right-3 z-10 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow-sm">
                  <span className="size-1.5 rounded-full bg-primary-foreground animate-ping" />
                  Último acesso
                </span>
              )}
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={isLoadingGoogle || isLoadingMagicLink}
                onClick={handleGoogleSignIn}
                className={`w-full gap-2.5 h-11 text-sm font-medium border-border/80 hover:bg-muted/60 transition-all ${
                  lastUsedMethod === "google"
                    ? "border-primary/60 bg-primary/[0.03] ring-1 ring-primary/25 hover:border-primary"
                    : ""
                }`}
              >
                {isLoadingGoogle ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <svg className="size-4" viewBox="0 0 24 24">
                    <title>Google Logo</title>
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    />
                  </svg>
                )}
                Continuar com Google
              </Button>
            </div>

            <div className="relative my-4 flex items-center justify-center text-xs uppercase text-muted-foreground">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/60" />
              </div>
              <span className="relative bg-card px-3 text-muted-foreground">
                ou continue com e-mail
              </span>
            </div>

            <form onSubmit={handleMagicLinkSignIn} className="space-y-3">
              <div className="space-y-1.5">
                <label
                  htmlFor="email-input"
                  className="text-xs font-medium text-foreground flex items-center gap-1.5"
                >
                  <Mail className="size-3.5 text-muted-foreground" />
                  Seu E-mail
                </label>
                <Input
                  id="email-input"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoadingGoogle || isLoadingMagicLink}
                  required
                  className="h-10 text-sm"
                />
              </div>

              <div className="relative">
                {lastUsedMethod === "magic-link" && (
                  <span className="absolute -top-2.5 right-3 z-10 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow-sm">
                    <span className="size-1.5 rounded-full bg-primary-foreground animate-ping" />
                    Último acesso
                  </span>
                )}
                <Button
                  type="submit"
                  size="lg"
                  disabled={isLoadingGoogle || isLoadingMagicLink || !email}
                  className={`w-full gap-2 h-10 text-sm font-medium ${
                    lastUsedMethod === "magic-link"
                      ? "ring-2 ring-primary/30"
                      : ""
                  }`}
                >
                  {isLoadingMagicLink ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <>
                      <span>Enviar Link de Acesso</span>
                      <ArrowRight className="size-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </CardContent>

      <CardFooter className="flex flex-col gap-2 mt-2 pt-4 pb-5 text-center text-xs text-muted-foreground">
        <div className="flex items-center justify-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-medium">
          <Sparkles className="size-3.5" />
          <span>Acesso seguro sem senhas com Magic Link ou Google</span>
        </div>
        <p className="text-[11px] text-muted-foreground/80">
          Ao continuar, você concorda com nossos{" "}
          <Link
            href="/seguranca-privacidade"
            className="underline underline-offset-2 hover:text-foreground"
          >
            Termos de Uso e Privacidade
          </Link>
          .
        </p>
      </CardFooter>
    </Card>
  );
}
